import Button from "@mui/material/Button"
import InputAdornment from "@mui/material/InputAdornment"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import SearchIcon from "@mui/icons-material/Search"
import React, { useEffect, useState } from "react"
import SearchFilters from "./shared/SearchFilters"
import Popover from "@mui/material/Popover"
import Box from "@mui/material/Box"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { useForm } from "react-hook-form"
import { Dayjs } from "dayjs"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import queryString from "query-string"
import Autocomplete from "@mui/material/Autocomplete"
import {
  useGetDepartmentQuery,
  useGetLineQuery,
  useGetSelectQuery,
} from "../../api/InterviewsApi"

interface iNewSelect {
  id: number | null
  name: string | null
}

interface ISearchFilters {
  department: iNewSelect | null
  project: iNewSelect | null
  line: iNewSelect | null
  graphType: iNewSelect | null
  status: iNewSelect | null
  interviewer: iNewSelect | null
  massRecruiter: iNewSelect | null
  training: iNewSelect | null
  search: string | null
  startDate: Dayjs | null
  endDate: Dayjs | null
}
const AllTablesFilter = ({
  withoutFilter,
  withProjectFilter,
}: {
  withoutFilter?: boolean
  withProjectFilter?: boolean
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? "simple-popover" : undefined

  const [filledFieldsCount, setFilledFieldsCount] = useState<number>(-1)

  const {
    handleSubmit,
    register,
    formState,
    control,
    watch,
    resetField,
    setValue,
    clearErrors,
    reset,
  } = useForm<ISearchFilters>({
    defaultValues: {
      department: null,
      project: null,
      line: null,
      graphType: null,
      interviewer: null,
      massRecruiter: null,
      training: null,
      search: null,
      startDate: null,
      status: null,
      endDate: null,
    },
  })
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = queryString.parse(location.search)
  const [searchParams] = useSearchParams()

  const updateFiltersInUrl = (newFilters: any) => {
    const updatedParams = {
      ...queryParams,
      ...newFilters,
    }
    Object.keys(updatedParams).forEach((key) => {
      if (updatedParams[key] === null || updatedParams[key] === undefined) {
        delete updatedParams[key]
      }
    })

    const updatedUrl = `${location.pathname}?${queryString.stringify(
      updatedParams,
    )}`
    navigate(updatedUrl)
  }

  const getFilledFieldsCount = () => {
    let count = 0
    const formValues = watch()

    for (const key in formValues) {
      const value = formValues[key as keyof ISearchFilters]
      if (value !== null && value !== undefined) {
        count += 1
      }
    }

    return count
  }

  useEffect(() => {
    const filledFields = getFilledFieldsCount()
    setFilledFieldsCount(filledFields)

    updateFiltersInUrl({
      status: watch("status.id"),
      graphType: watch("graphType.id"),
      department: watch("department.id"),
      project: watch("project.id"),
      line: watch("line.id"),
      massRecruiter: watch("massRecruiter.id"),
      interviewer: watch("interviewer.id"),
      training: watch("training.id"),
      search: watch("search"),
      startDate: watch("startDate"),
      endDate: watch("endDate"),
    })
  }, [
    watch("graphType.id"),
    watch("department.id"),
    watch("project.id"),
    watch("line.id"),
    watch("massRecruiter.id"),
    watch("interviewer.id"),
    watch("training"),
    watch("search"),
    watch("startDate"),
    watch("endDate"),
    watch("status.id"),
  ])

  const { errors } = formState

  const selects = useGetSelectQuery()

  const department = useGetDepartmentQuery(`${watch("project.id")}`, {
    skip: !watch("project.id"),
  })

  const line = useGetLineQuery(watch("department.id"), {
    skip: !watch("department.id"),
  })

  return (
    <Stack direction={"row"} alignItems="center" spacing="10px">
      {!withoutFilter && (
        <TextField
          disabled={withoutFilter}
          size="small"
          id="input-with-icon-textfield"
          label="Поиск"
          value={watch("search")}
          {...register("search")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      )}
      {withProjectFilter && (
        <Stack direction={"row"} alignItems="center" spacing="10px">
          {selects.data && (
            <Autocomplete
              sx={{ width: "200px" }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={watch("project")}
              loading={selects.isLoading}
              {...register("project")}
              size="small"
              getOptionKey={(option) => option.id}
              fullWidth
              onChange={(event: any, value: any) => {
                setValue("project", value)
                resetField("department")
                resetField("line")
              }}
              options={selects?.data.filterOptions?.project}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  variant="outlined"
                  {...params}
                  placeholder="Проект"
                />
              )}
            />
          )}

          <Autocomplete
            sx={{ width: "200px" }}
            disabled={watch("project.id") ? false : true}
            loading={department?.isLoading}
            value={watch("department")}
            {...register("department")}
            size="small"
            getOptionKey={(option) => option.id}
            fullWidth
            onChange={(event: any, value: any) => {
              setValue("department", value)
              resetField("line")
            }}
            options={department?.data || []}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                {...params}
                placeholder="Направление"
              />
            )}
          />

          <Autocomplete
            sx={{ width: "200px" }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={watch("department.id") ? false : true}
            value={watch("line")}
            {...register("line")}
            size="small"
            getOptionKey={(option) => option.id}
            fullWidth
            onChange={(event: any, value: any) => {
              setValue("line", value)
            }}
            options={line?.data?.data || []}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField variant="outlined" {...params} placeholder="Линия" />
            )}
          />
        </Stack>
      )}
      {!withoutFilter && (
        <Button
          sx={{ height: "40px", width: "160px" }}
          startIcon={<FilterAltIcon />}
          variant="outlined"
          onClick={handleClick}
        >
          Фильтры
          {filledFieldsCount > 0 ? (
            <Box
              sx={{
                ml: "10px",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                border: "1px solid #1B9C18",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.primary",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              {filledFieldsCount}
            </Box>
          ) : (
            <></>
          )}
        </Button>
      )}
      <Box>
        <Popover
          elevation={0}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          id={id}
          open={open}
          anchorEl={anchorEl}
          sx={{ mt: "10px" }}
        >
          <Stack
            sx={{
              p: "18px",
              borderRadius: "4px",
              pt: "10px",
              border: "1px solid rgba(27, 156, 24, 1)",
            }}
            direction={"row"}
            spacing={"5px"}
          >
            <SearchFilters
              filledFieldsCount={filledFieldsCount}
              setFilledFieldsCount={setFilledFieldsCount}
              handleClose={handleClose}
              watch={watch}
              register={register}
              setValue={setValue}
              resetField={resetField}
              handleSubmit={handleSubmit}
              reset={reset}
              updateFiltersInUrl={updateFiltersInUrl}
              clearErrors={clearErrors}
            />
          </Stack>
        </Popover>
        {filledFieldsCount > 0 && (
          <Button
            disabled={filledFieldsCount === 0}
            onClick={() => {
              reset()
              updateFiltersInUrl({})
              setValue("status", null)
            }}
            variant="text"
            startIcon={<RestartAltIcon />}
          >
            Сбросить фильтры
          </Button>
        )}
      </Box>
    </Stack>
  )
}

export default AllTablesFilter
